<script setup>
import { ref, computed, inject, onUnmounted, watch } from "vue";
import closeIcon from "@/assets/icon/close.svg";
import {
  appHapticFeedback,
  toastFail,
  toastSuccess,
  _twa_encode,
  getLocalStorage,
  formatTimestamp,
  setLocalStorage,
  addCommasToNumber,
} from "@/utils/common";
import { useConfigStore } from "@/store/config";
import { useUserStore } from "@/store/user";
import { convert, acceleratedWithdrawals, getWithdrawList } from "@/api/TON";
import { preloadTurnstile } from "@/utils/turnstile";
import {
  getRetryWithdrawAmount,
  isRetryWithdrawEventForSource,
} from "@/utils/withdrawRetry";
import Loading from "@/components/Loading.vue";
import emitter from "@/utils/bus";
import { useI18n } from "vue-i18n";
import { useTonConnectStore } from "@/store/tonConnect";
import WithdrawDetail from "@/components/WithdrawDetail.vue";
import { getEvent } from "@/api/event";
const { t } = useI18n();

const $swal = inject("$swal");
const config = useConfigStore();
const userStore = useUserStore();
const tonConnectStore = useTonConnectStore();
const withdraw_amount = ref("");
const withdraw_step = computed(() => config.withdraw_step || []);
const web_withdraw_record = computed(() => userStore.web_withdraw_record); // 提现记录
const withdraw_ladder = computed(() => config.withdraw_ladder || []); // 提现阶级
const accelerated_step = computed(() => config.accelerated_step || []); // 加速步骤
const withdraw_list = computed(() => userStore.web_withdraw_record); // 提现记录
const withdraw_ladder_index = computed(() => {
  if (!withdraw_ladder.value?.length) {
    return null;
  }

  const recordLength = web_withdraw_record.value?.length || 0;
  const limitLength = withdraw_ladder.value.length;

  // 如果没有提现记录，返回第一个限制值
  if (!recordLength) {
    return withdraw_ladder.value[0];
  }

  // 如果提现记录数超过限制数组长度，返回最后一个限制值
  if (recordLength >= limitLength) {
    return withdraw_ladder.value[limitLength - 1];
  }

  // 否则返回对应记录数的限制值
  return withdraw_ladder.value[recordLength];
}); // 提现阶级
const actionLoading = ref(false);
const showWithdrawDetail = ref(false);
const withdrawSourceId = `withdraw-${Math.random().toString(36).slice(2)}`;
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const onClose = () => {
  appHapticFeedback();
  withdraw_amount.value = "";
  emit("close");
};

const retryWithdraw = async (event = {}) => {
  if (
    actionLoading.value ||
    !isRetryWithdrawEventForSource({
      sourceId: withdrawSourceId,
      event,
    })
  ) {
    return;
  }

  const retryAmount = getRetryWithdrawAmount({
    balance: userStore.balance,
    maxAmount: withdraw_ladder_index.value?.max_amount,
  });

  if (!retryAmount) {
    return;
  }

  withdraw_amount.value = String(retryAmount);
  await save();
};

const save = async () => {
  let share_group_nums = getLocalStorage("share_group_nums");
  let input_amount = Number(withdraw_amount.value);
  let balance = Number(userStore.balance);
  let withdraw_limit_Min = withdraw_ladder_index.value?.min_amount;
  let withdraw_limit_Max = withdraw_ladder_index.value?.max_amount;
  if (!userStore.payment_info) {
    tonConnectStore.linkedWallet(true);
    return;
  }

  // 判断输入金额是否小于最小提现金额
  if (input_amount < withdraw_limit_Min) {
    console.log(111);

    let msg = t("message.min_withdrawal_amount", {
      num: addCommasToNumber(withdraw_limit_Min),
      coin_name: config.coin_name,
    });
    toastFail($swal, msg);
    return;
  }
  // 判断输入金额是否大于最大提现金额
  if (input_amount > withdraw_limit_Max) {
    let msg = t("message.max_withdrawal_amount", {
      num: addCommasToNumber(withdraw_limit_Max),
      coin_name: config.coin_name,
    });
    toastFail($swal, msg);
    return;
  }
  // 判断输入金额是否大于余额
  if (input_amount > balance) {
    let msg = t("message.balance_not_enough");
    toastFail($swal, msg);
    return;
  }
  console.log(getLocalStorage("task_complete_num"));

  try {
    actionLoading.value = true;
    let res = await convert({
      amount: _twa_encode(String(withdraw_amount.value)),
      address: _twa_encode(userStore.payment_info),
      token_name: _twa_encode(config.coin_name),
      share: _twa_encode(String(share_group_nums)),
      send_message_num: _twa_encode(getLocalStorage("post_message_num")),
      over_task: _twa_encode(getLocalStorage("task_complete_num")),
    });
    if (res.data?.completed_steps) {
      // 读取本地已完成步骤与已上报步骤（均为对象），容错为空/损坏
      let completedStepsCache = {};
      try {
        completedStepsCache = JSON.parse(getLocalStorage("completed_steps") || "{}") || {};
      } catch (e) {
        completedStepsCache = {};
      }
      let reportedSteps = {};
      try {
        reportedSteps = JSON.parse(getLocalStorage("reported_steps") || "{}") || {};
      } catch (e) {
        reportedSteps = {};
      }

      // 合并服务端返回的完成步骤到缓存对象
      res.data.completed_steps.forEach((id) => {
        completedStepsCache[id] = true;
      });

      // 仅对未上报的步骤进行上报，并在成功后标记为已上报
      for (const id of Object.keys(completedStepsCache)) {
        if (!reportedSteps[id]) {
          try {
            await getEvent({
              event_type: `withdraw_step${id}`,
              project_id: TApp_GlobalConfig.project_id,
            });
            reportedSteps[id] = true;
          } catch (error) {
            console.log(error);
          }
        }
      }

      // 回写本地缓存
      setLocalStorage("completed_steps", JSON.stringify(completedStepsCache));
      setLocalStorage("reported_steps", JSON.stringify(reportedSteps));
    }
    if (res.code != 200) {
      let num = res.data.target || 0;
      let need_num = res.data.target - res.data.schedule || 0;
      let message =
        withdraw_step.value.find(
          (item) => item.equipment_id == res.data.equipment_id
        )?.message || "";
      emitter.emit("showWithdrawTip", {
        type: res.data.type,
        show: true,
        num: num || 0,
        need_num: need_num || 0,
        message: message || "",
        price: res.data.price || 0,
        equipment_id: res.data.equipment_id || 0,
        completed_steps: res.data.completed_steps || [],
        retry_type: "withdraw",
        sourceId: withdrawSourceId,
      });
      onClose();
    }
    if (res.code == 200) {
      userStore.updateBalance(input_amount);
      const submintRecord = await getWithdrawList();
      userStore.initWithdrawWebRecord(submintRecord.data);
      toastSuccess($swal, t("message.Withdraw_success"));
      onClose();
    }
  } catch (error) {
    console.error(error);
    toastFail($swal, t("message.SubmissionFailed"));
  } finally {
    actionLoading.value = false;
  }
};

async function maxBalance() {
  appHapticFeedback();
  if (withdraw_ladder_index.value?.max_amount > userStore.balance) {
    withdraw_amount.value = userStore.balance;
  } else {
    withdraw_amount.value = withdraw_ladder_index.value?.max_amount;
  }
}

const formatInput = (event) => {
  // 获取小数部分的长度
  const decimalLimit = config.claim_limit.includes(".")
    ? config.claim_limit.split(".")[1].length
    : 0;
  // 移除非数字和小数点的字符
  let value = event.target.value.replace(/[^0-9.]/g, "");
  const parts = value.split(".");
  // 只允许一个小数点
  if (parts.length > 2) {
    value = parts[0] + "." + parts.slice(1).join("");
  }
  // 限制小数位数
  if (parts[1] && parts[1].length > decimalLimit) {
    value = parts[0] + "." + parts[1].slice(0, decimalLimit); // 根据 claim_limit 限制小数位数
  }
  // 不允许在没有小数点的情况下输入小数点
  if (parts[0] === "" && value.includes(".")) {
    value = value.replace(".", ""); // 移除小数点
  }
  withdraw_amount.value = value;
};
let rowDetail = ref({});
const recordDetailOnClick = (item) => {
  appHapticFeedback();
  rowDetail.value = item;
  emitter.emit("showWithdrawDetail", true);
  console.log(item);

};
const goWithdrawDetail = () => {
  showWithdrawDetail.value = true;
}
const handleAcceleratedWithdrawals = (item) => {
  accelerated(item);
};
emitter.on("acceleratedWithdrawals", handleAcceleratedWithdrawals);
const accelerated = async (item) => {
  if (item.audit_status == 11) {
    return;
  }
  appHapticFeedback();
  let res = await acceleratedWithdrawals({
    id: _twa_encode(item.id.toString()),
    share: _twa_encode(getLocalStorage("share_group_nums")),
    over_task: _twa_encode(getLocalStorage("task_complete_num")),
    send_message_num: _twa_encode(getLocalStorage("post_message_num")),
  });
  if (res.data?.completed_steps) {
    // 读取本地已完成步骤与已上报步骤（均为对象），容错为空/损坏
    let completedStepsCache = {};
    try {
      completedStepsCache = JSON.parse(getLocalStorage("completed_steps") || "{}") || {};
    } catch (e) {
      completedStepsCache = {};
    }
    let reportedSteps = {};
    try {
      reportedSteps = JSON.parse(getLocalStorage("reported_steps") || "{}") || {};
    } catch (e) {
      reportedSteps = {};
    }

    // 合并服务端返回的完成步骤到缓存对象
    res.data.completed_steps.forEach((id) => {
      completedStepsCache[id] = true;
    });

    // 仅对未上报的步骤进行上报，并在成功后标记为已上报
    for (const id of Object.keys(completedStepsCache)) {
      if (!reportedSteps[id]) {
        try {
          await getEvent({
            event_type: `accelerated_step${id}`,
            project_id: TApp_GlobalConfig.project_id,
          });
          reportedSteps[id] = true;
        } catch (error) {
          console.log(error);
        }
      }
    }
    // 回写本地缓存
    setLocalStorage("completed_steps", JSON.stringify(completedStepsCache));
    setLocalStorage("reported_steps", JSON.stringify(reportedSteps));
  }
  if (res.code != 200) {
    let num = res.data.target || 0;
    let need_num = res.data.target - res.data.schedule || 0;
    let message =
      accelerated_step.value.find(
        (item) => item.equipment_id == res.data.equipment_id
      )?.message || "";
    emitter.emit("showWithdrawTip", {
      type: res.data.type,
      show: true,
      num: num || 0,
      need_num: need_num || 0,
      message: message || "",
      price: res.data.price || 0,
      equipment_id: res.data.equipment_id || 0,
      completed_steps: res.data.completed_steps || [],
      accelerated_step: true,
      content_type: 12,
      retry_type: "accelerated",
      sourceId: withdrawSourceId,
    });
  }
  if (res.code == 200) {
    //更新所有提现记录审核状态为11（成功和失败的提现记录不修改状态）
    const submintRecord = withdraw_list.value;
    let updatedCount = 0;

    // 遍历所有提现记录，更新符合条件的记录状态为11
    submintRecord.forEach((record) => {
      // 只更新处理中状态的记录(1, 7, 9)，不修改成功(2, 8, 10)和失败(3)的记录
      if (record.audit_status == 1 || record.audit_status == 7 || record.audit_status == 9) {
        record.audit_status = 11;
        updatedCount++;
      }
    });

    if (updatedCount > 0) {
      userStore.initWithdrawWebRecord(submintRecord);
      toastSuccess($swal, t("message.Accelerated_withdrawals_success"));
    } else {
      toastSuccess($swal, t("message.Accelerated_withdrawals_success"));
    }
  }
}
const accelerated_style = (item, index) => {
  if (index == web_withdraw_record.value.length - 1 && config.withdraw_ladder[0].payment) {
    return {
      display: "none",
    };
  }
  if (item.audit_status == 11) {
    return {
      color: "#8e8e8e",
    };
  }
}

watch(
  () => props.show,
  (visible) => {
    if (visible) {
      preloadTurnstile();
    }
  },
  { immediate: true },
);

emitter.on("retryWithdraw", retryWithdraw);

onUnmounted(() => {
  emitter.off("acceleratedWithdrawals", handleAcceleratedWithdrawals);
  emitter.off("retryWithdraw", retryWithdraw);
});
</script>

<template>
  <div class="wallet-config" v-show="props.show">
    <WithdrawDetail :detail="rowDetail" />
    <div class="wallet-config__container">
      <!-- Header -->
      <header class="wallet-config__header">
        <h2 class="wallet-config__title">{{ $t("message.Withdrawal") }}</h2>
        <div class="wallet-config__close" @click="onClose">
          <img :src="closeIcon" alt="close" />
        </div>
      </header>

      <!-- Manual Input Section -->
      <section class="wallet-config__section">
        <!-- <div class="wallet-config__limit">
          <div>{{ $t("message.Minimumamount") }}</div>
          <img class="limit-icon" :src="config.coin_image" alt="" />
          <div class="limit-amount">
            {{ withdraw_limit_v1_index?.key_val }} {{ config.coin_name }}
          </div>
        </div> -->

        <div class="wallet-config__input-group">
          <input class="wallet-config__input" :placeholder="$t('message.Enteramount')" v-model="withdraw_amount"
            @input="formatInput" />
          <button class="wallet-config__paste" @click="maxBalance">
            {{ $t("message.All") }}
          </button>
        </div>
        <div class="wallet-config__tip">
          {{ $t("message.Networkfee") }} {{ addCommasToNumber(config.withdraw_fee || 0) }}
          {{ config.coin_name }}
        </div>
        <div class="wallet-config__remark">
          {{ withdraw_ladder_index?.remark }}
        </div>
      </section>

      <!-- Save Button -->

      <button class="wallet-config__button wallet-config__button--save"
        :style="{ backgroundColor: config.themes_color }" @click="save" :disabled="actionLoading">
        <Loading v-if="actionLoading" class="button-loading" size="small" color="white" />
        <div class="button-text">{{ $t("message.Submit") }}</div>
      </button>
      <div class="withdraw-detail" @click="goWithdrawDetail">{{ t("message.WithdrawDetail") }}</div>
    </div>
    <div class="itemBox" v-if="showWithdrawDetail" @click="showWithdrawDetail = false">
      <div class="withdraw-box" @click.stop>
        <div class="withdraw-close" @click="showWithdrawDetail = false">
          <img :src="closeIcon" alt="close" />
        </div>
        <div class="withdraw-title">{{ $t("message.WithdrawalRecord") }}</div>
        <div class="withdrawBox">
          <div class="record-list" v-if="web_withdraw_record && web_withdraw_record.length > 0">
            <div class="record-item" v-for="(item, index) in web_withdraw_record" :key="index"
              @click="recordDetailOnClick(item, true)">
              <div>
                <div class="record-amount">
                  {{ addCommasToNumber(item.send_amount) }} {{ config.coin_name }} <span
                    v-if="item.audit_status == 1 || item.audit_status == 7 || item.audit_status == 9 || item.audit_status == 11"
                    :style="accelerated_style(item, index)" @click.stop="accelerated(item)" class="accelerated">{{
                      t("message.Accelerated") }}
                    🚀</span>
                </div>
                <div class="record-time">
                  {{ formatTimestamp(item.created_at) }}
                </div>
              </div>
              <div>
                <div class="record-status"
                  v-if="item.audit_status == 1 || item.audit_status == 7 || item.audit_status == 9 || item.audit_status == 11">
                  {{ $t("message.Processing") }}
                </div>
                <div class="record-status"
                  v-if="item.audit_status == 2 || item.audit_status == 8 || item.audit_status == 10">
                  {{ $t("message.Success") }}
                </div>
                <div class="record-status" v-if="item.audit_status == 3">
                  {{ $t("message.Rejection") }}
                </div>
              </div>
            </div>
          </div>
          <div class="empty-list" v-else>
            {{ $t("message.NoWithdrawalRecord") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/surfaces" as surface;

.itemBox {
  @include surface.dialog-overlay;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  animation: fadeIn 0.3s ease-out;
  z-index: 998;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.withdraw-box {
  @include surface.dialog-panel;
  background: linear-gradient(135deg, #1b1b1b 0%, #2a2a2a 100%);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: slideUp 0.3s ease-out;
  position: relative;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.withdraw-close {
  @include surface.close-button;
  top: 8px;
  right: 8px;
  position: absolute;
  background-color: rgba(255, 255, 255, 0.1);
  z-index: 10;

  img {
    width: 16px;
    height: 16px;
    opacity: 0.8;
  }
}

.withdraw-title {
  @include surface.title;
  padding: 8px 40px;
  margin-bottom: 8px;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
  position: relative;
  padding-top: 6px;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 2px;
    background: v-bind("config.themes_color");
    border-radius: 1px;
  }
}

.withdrawBox {
  background: linear-gradient(135deg, #2d2d2d 0%, #3a3a3a 100%);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  max-height: 60vh;
  overflow-y: auto;

  /* Firefox 隐藏滚动条 */
  scrollbar-width: none;

  /* IE 隐藏滚动条 */
  -ms-overflow-style: none;

  /* 隐藏滚动条但保持滚动功能 */
  &::-webkit-scrollbar {
    width: 0;
    display: none;
  }

  &::-webkit-scrollbar-track {
    display: none;
  }

  &::-webkit-scrollbar-thumb {
    display: none;
  }

  .empty-list {
    padding: 16px 10px;
    text-align: center;
    color: #a0a0a0;
    font-size: 14px;
  }

  .record-list {
    .record-item {
      @include surface.press-feedback;
      padding: 8px;
      gap: 6px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      background: transparent;
      cursor: pointer;

      &:last-child {
        border-bottom: none;
      }

      .record-amount {
        font-size: 14px;
        overflow-wrap: anywhere;
        font-weight: 600;
        color: #ffffff;
        margin-bottom: 4px;
      }

      .accelerated {
        text-decoration: underline;
        text-underline-offset: 0.5vw;
      }

      .record-time {
        font-size: 12px;
        color: #a0a0a0;
        opacity: 0.8;
      }

      .record-status {
        font-size: 12px;
        font-weight: 500;
        padding: 4px 8px;
        border-radius: 6px;
        background: rgba(255, 255, 255, 0.1);
        color: v-bind("config.themes_color");
        border: 1px solid rgba(255, 255, 255, 0.1);
      }
    }
  }
}

.wallet-config {
  @include surface.dialog-overlay;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(5px);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;

  &__container {
    @include surface.dialog-panel;
    background: #1e1e1e;
    border: 1px solid #2d2d2d;
  }

  &__header {
    @include surface.dialog-header;
    margin-bottom: 8px;
    position: relative;
  }

  &__title {
    @include surface.title;
    font-weight: bold;
    //text-align: center;
    color: #ffffff;
  }

  &__close {
    @include surface.close-button;
    position: static;
    top: auto;
    right: auto;
    transform: none;
    background-color: rgba(255, 255, 255, 0.1);

    img {
      width: 70%;
      height: 70%;
    }
  }

  &__section {
    margin-top: 8px;
  }

  &__label {
    color: #6b7280;
    font-size: 16px;
    margin: 0 0 8px;
    font-weight: normal;
  }

  &__limit {
    color: #6b7280;
    font-size: 3.5vw;
    font-weight: normal;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 8px;

    .limit-icon {
      width: 4vw;
      height: 4vw;
      margin-left: 1vw;
      margin-right: 1vw;
    }

    .limit-amount {
      color: v-bind("config.themes_color");
    }
  }

  &__input-group {
    position: relative;
    margin-bottom: 8px;
  }

  &__input {
    height: 40px;
    border-radius: 10px;
    width: 100%;
    padding: 0 88px 0 10px;
    border: 1px solid #2d2d2d;
    font-size: 16px;
    box-sizing: border-box;

    &--limit {
      display: flex;
      justify-content: start;
      align-items: center;
    }

    &::placeholder {
      color: #999;
    }

    &:focus {
      outline: none;
      border-color: var(--themes-color);
    }
  }

  &__paste {
    @include surface.press-feedback($rest-transform: translateY(-50%));
    min-height: 28px;
    padding: 4px 8px;
    border-radius: 8px;
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background-color: #f0f0f0;
    border: none;
    font-size: 14px;
    color: #1f1f1f;
    cursor: pointer;
  }

  &__tip {
    font-size: 13px;
    line-height: 1.5;
    color: #999;
    margin-top: -1vw;
    text-align: start;
    margin-left: 1vw;
  }

  &__remark {
    font-size: 14px;
    line-height: 1.5;
    margin-top: 8px;
    text-align: start;
    margin-left: 1vw;
  }

  .button-loading {
    margin-right: 5vw;
  }

  &__button {
    @include surface.button;
    width: 100%;
    color: #fff;
    border: none;
    font-weight: bold;
    cursor: pointer;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
    }

    svg {
      width: 24px;
      height: 24px;
      margin-right: 6px;
    }

    &--connect {
      margin-top: 8px;
    }

    &--save {
      margin-top: 8px;
    }
  }

  .withdraw-detail {
    @include surface.button;
    margin-top: 6px;
    text-align: center;
    color: #fff;
    background: v-bind("config.themes_color");
    font-weight: bold;
  }
}
.wallet-config__close img {
  width: 24px;
  height: 24px;
}
</style>
